import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Laptop, LogOut } from 'lucide-react'
import {
  Button, Card, CardBody, CardHeader, EmptyState, ErrorState, LoadingState,
  NoPermissionState, useLocale,
} from '@hk/ui'
import {
  ApiError, endSession, fetchDevices, formatCount, formatDateTime, formatMinutes,
  nameIn, pluralise, translateError, type AppLocale, type DeviceRow, type Plural,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'
import { deviceLabel } from './UserDetail'

const fill = (template: string, values: Record<string, string>) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, value), template)

const counted = (
  forms: Record<Plural, string>, count: number, locale: AppLocale,
) => pluralise(forms, count, locale, formatCount(count, locale))

/**
 * **HOW LONG SINCE IT WAS LAST USED, IN A UNIT A PERSON CAN ACT ON.**
 *
 * The first render drew nine days as "216h 27m ago". That is true, and nobody converts it
 * in their head — on the one screen whose flag is *not used for over a day*, the number
 * being flagged was in a unit that hid what it meant. Under two days the hours matter (a
 * handset last seen at breakfast is a different thing from one last seen on Tuesday);
 * over two days they are noise, and the day count is the whole answer.
 */
function since(minutes: number, locale: AppLocale, t: typeof STRINGS['en']): string {
  if (minutes >= 60 * 48)
    return counted(t.dvDaysAgo, Math.floor(minutes / (60 * 24)), locale)
  return fill(t.minutesAgo, { minutes: formatMinutes(minutes, locale) })
}

/* ============================================================================
   W-134 — WHAT IS SIGNED IN TO THIS HOTEL, AND FROM WHAT KIND OF THING.
   Sprint 71.

   ── THE SCREEN IS CALLED "DEVICES" AND THERE IS NO SUCH THING IN THIS PRODUCT ──
   Nothing anywhere issues a device an identity. The only evidence a session carries is
   the `User-Agent` it presented, which becomes one of eight coarse codes or — honestly —
   nothing at all. **Two identical Android handsets are indistinguishable here and always
   will be.**

   That is not a reason to leave the screen out. It is the reason the limitation is
   PRINTED ON IT, above the table, in both languages — because the decision somebody makes
   in front of a register ("that handset is not ours, cut it off") is a decision this
   evidence cannot support, and a plausible device list looks exactly like a true one.

   So a row is a GROUPING — one person, one kind of thing — and where it holds more than
   one session it says so in words rather than drawing a device count.

   ── WHAT IT ANSWERS THAT W-121 CANNOT ─────────────────────────────────────────
   W-121 shows one person's sessions, and somebody has to know whose name to type. This
   answers the question asked when nobody does — *is anything still signed in that should
   not be* — which is why the register is ordered OLDEST-SEEN FIRST and why "not seen for
   a day" is a flag rather than a column somebody has to scan.

   ── AND ENDING ONE IS W-121's ACT, NOT A NEW ONE ──────────────────────────────
   The rows carry session ids and this screen calls the endpoint the users screen already
   uses. A second way to end a session would be a second expression of a call that writes
   an audit row, and the two would disagree the day one of them changed.
   ========================================================================= */

export function Devices({ propertyId }: { propertyId: string }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]
  const queryClient = useQueryClient()
  const [ending, setEnding] = useState<string | null>(null)

  const register = useQuery({
    queryKey: ['devices', propertyId],
    queryFn: ({ signal }) => fetchDevices(propertyId, { signal }),
    retry: false,
  })

  const end = useMutation({
    /* **ONE CALL PER SESSION, IN SEQUENCE.** The row is a grouping and the act is
       per-session; firing them in parallel would write the audit rows in an order nobody
       chose, on the one table where the order is the record. */
    mutationFn: async (row: DeviceRow) => {
      for (const id of row.sessionIds) await endSession(id)
    },
    onSettled: () => {
      setEnding(null)
      void queryClient.invalidateQueries({ queryKey: ['devices', propertyId] })
    },
  })

  if (register.isLoading && !register.error) return <LoadingState rows={5} />

  if (register.error && !register.data) {
    if (register.error instanceof ApiError && register.error.code === 'permission.denied')
      return <NoPermissionState />
    const translated = translateError(register.error, locale)
    return (
      <ErrorState
        body={translated.message}
        onRetry={register.refetch}
        {...(translated.correlationId ? { correlationId: translated.correlationId } : {})}
      />
    )
  }

  const view = register.data!

  return (
    <div className="space-y-4 p-4">
      {/* **THE LIMITATION FIRST, ABOVE THE TABLE.** Under it, it is a footnote on a screen
          somebody has already believed. */}
      <Card>
        <CardHeader title={t.dvWhatThisIs} icon={<AlertTriangle className="size-4" />} />
        <CardBody className="space-y-1.5 text-sm">
          <p>{t.dvNoDeviceIdentity}</p>
          <p className="text-muted-foreground">{t.dvWhatItIsFor}</p>
        </CardBody>
      </Card>

      {view.rows.length === 0 ? (
        <EmptyState title={t.dvNobody} body={t.dvNobodyBody} />
      ) : (
        <>
          <p className="text-sm">
            {/* **TWO WHOLE SENTENCES, NOT TWO CLAUSES JOINED HERE.** The first draft put
                a Latin comma between them in the markup, and it rendered as one in Arabic,
                where the comma is «،» — a visible character written straight into a screen
                is invisible to the dictionary guard, which is the shape of this defect
                every time it appears. `strings.test.ts` now looks for these. */}
            {counted(t.dvHeadlineSessions, view.liveSessions, locale)}{' '}
            {counted(t.dvHeadlineAcross, view.people, locale)}
            {view.stale > 0 && (
              <>
                {' '}
                <span className="font-medium" style={{ color: 'var(--hk-hk-dirty-text)' }}>
                  {counted(t.dvStale, view.stale, locale)}
                </span>
              </>
            )}
          </p>

          {/* **THE AMBIGUITY IS A SENTENCE, NOT A SILENCE.** Rows holding more than one
              session are the rows the screen cannot resolve, and saying how many there are
              is the only honest summary available. */}
          {view.groupings > 0 && (
            <p className="text-[12px] text-muted-foreground">
              {counted(t.dvGroupings, view.groupings, locale)}
            </p>
          )}

          <Card>
            <CardHeader title={t.dvSignedIn} icon={<Laptop className="size-4" />} />
            <CardBody className="space-y-3">
              {view.rows.map((row) => (
                <Row
                  key={`${row.userId}:${row.device ?? 'unknown'}`}
                  row={row}
                  busy={ending === rowKey(row)}
                  onEnd={() => { setEnding(rowKey(row)); end.mutate(row) }}
                />
              ))}
            </CardBody>
          </Card>

          {view.unknown > 0 && (
            <p className="text-[12px] text-muted-foreground">
              {counted(t.dvUnknownNote, view.unknown, locale)}
            </p>
          )}
        </>
      )}
    </div>
  )
}

const rowKey = (row: DeviceRow) => `${row.userId}:${row.device ?? 'unknown'}`

function Row({ row, busy, onEnd }: { row: DeviceRow; busy: boolean; onEnd: () => void }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]
  const stale = row.minutesSinceSeen >= 60 * 24

  return (
    <div className="flex flex-wrap items-start justify-between gap-2 border-b pb-3 last:border-0 last:pb-0">
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="text-sm font-medium">
          {nameIn(locale, row.person, row.personAr)}
          {' · '}
          <span className="font-normal">{deviceLabel(row.device, locale)}</span>
        </p>

        <p className="text-[12px] text-muted-foreground">
          {/* Coarse on purpose — `lastSeen` is written at most once a minute, so a
              timestamp would imply a precision that is not there. */}
          {stale
            ? (
              <span style={{ color: 'var(--hk-hk-dirty-text)' }}>
                {since(row.minutesSinceSeen, locale, t)}
              </span>
            )
            : since(row.minutesSinceSeen, locale, t)}
          {' · '}
          {fill(t.dvSince, { when: formatDateTime(new Date(row.firstIssuedAtUtc), locale) })}
        </p>

        {/* **MORE THAN ONE SESSION IS A SENTENCE.** Drawing "2" here would be drawing a
            device count, which is the one number this screen does not have. */}
        {row.liveSessions > 1 && (
          <p className="text-[12px]">
            {counted(t.dvManySessions, row.liveSessions, locale)}
          </p>
        )}

        {/* **THE RAW STRINGS, VERBATIM AND LEFT-TO-RIGHT.** The label is a guess; this is
            what the device actually said, and where two differ it is the only proof on
            this screen that the row is two devices rather than one. */}
        {row.userAgents.map((agent) => (
          <p key={agent} className="text-[11px] text-muted-foreground">
            <bdi dir="ltr" className="break-all">{agent}</bdi>
          </p>
        ))}
      </div>

      <Button
        variant="outline"
        size="sm"
        onClick={onEnd}
        disabled={busy}
      >
        <LogOut className="size-3.5 rtl:rotate-180" />
        {row.liveSessions > 1 ? t.dvEndAll : t.dvEnd}
      </Button>
    </div>
  )
}
